import React from 'react';
import { Alert, AlertTitle, Button, Typography, Box, useTheme } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FormattedMessage } from 'react-intl';
import { RestrictedAccessWrapper } from './styling';

type BackendErrorPageProps = {
  errorType?: 'subscription' | 'appSettings' | 'backend';
  statusCode?: number;
  errorMessage?: string;
};

function BackendErrorPage({ 
  errorType = 'backend', 
  statusCode, 
  errorMessage 
}: BackendErrorPageProps) {
  const theme = useTheme();
  
  const handleRefresh = () => {
    window.location.reload();
  };

  const getTitleMessageId = () => {
    switch (errorType) {
      case 'subscription':
        return 'backendError.subscription.title';
      case 'appSettings':
        return 'backendError.appSettings.title';
      default:
        return 'backendError.backend.title';
    }
  };

  const getDescriptionMessageId = () => {
    switch (errorType) {
      case 'subscription':
        return 'backendError.subscription.description';
      case 'appSettings':
        return 'backendError.appSettings.description';
      default:
        return 'backendError.backend.description';
    }
  };

  return (
    <RestrictedAccessWrapper>
      <Alert 
        severity="error" 
        icon={<ErrorOutlineIcon fontSize="large" />}
        sx={{ maxWidth: 800, width: '100%' }}
      >
        <AlertTitle sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
          <FormattedMessage id={getTitleMessageId()} />
        </AlertTitle>
        
        <Typography variant="body1" sx={{ mb: 2 }}>
          <FormattedMessage id={getDescriptionMessageId()} />
        </Typography>
        
        {statusCode && (
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              <FormattedMessage 
                id="backendError.statusCode" 
                values={{ code: <span style={{ fontFamily: 'monospace' }}>{statusCode}</span> }}
              />
            </Typography>
          </Box>
        )}
        
        {errorMessage && (
          <Box sx={{ mb: 2, p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
              {errorMessage}
            </Typography>
          </Box>
        )}
        
        <Typography variant="body1" sx={{ mb: 1 }}>
          <FormattedMessage id="backendError.possibleCauses" />
        </Typography>
        <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
          <li><FormattedMessage id="backendError.cause.serviceDown" /></li>
          <li><FormattedMessage id="backendError.cause.networkIssues" /></li>
          <li><FormattedMessage id="backendError.cause.maintenance" /></li>
          <li><FormattedMessage id="backendError.cause.authIssues" /></li>
        </ul>
        
        <Typography variant="body1" sx={{ mb: 2 }}>
          <FormattedMessage id="backendError.instruction" />
        </Typography>
        
        <Button 
          variant="contained" 
          color="error" 
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          sx={{ mt: 1 }}
        >
          <FormattedMessage id="backendError.refreshButton" />
        </Button>
      </Alert>
    </RestrictedAccessWrapper>
  );
}

export default BackendErrorPage;
